import React, { useEffect, useRef, useState } from 'react';
import { Timer } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const fmt = (secs) => {
  const s = Math.max(0, secs);
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};

/**
 * Live countdown for a psychology session. Anchored on the server-computed
 * `elapsedSeconds` so both sides show the same time regardless of device
 * clock skew. Length comes from the package the patient paid for.
 */
export const SessionTimer = ({ elapsedSeconds = 0, minutes, compact = false, onWarning, onExpire }) => {
  const { t } = useLanguage();
  const total = (Number(minutes) || 30) * 60;
  const [elapsed, setElapsed] = useState(Number(elapsedSeconds) || 0);
  const warned = useRef(false);
  const expired = useRef(false);

  useEffect(() => { setElapsed(Number(elapsedSeconds) || 0); }, [elapsedSeconds]);

  useEffect(() => {
    const id = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const remaining = total - elapsed;

  // "5 minutes left" only makes sense for long sessions (psychology).
  useEffect(() => {
    if (warned.current || total <= 600) return;
    if (remaining <= 300 && remaining > 0) {
      warned.current = true;
      if (onWarning) onWarning(remaining);
    }
  }, [remaining, total, onWarning]);

  // Hard stop (short consultations): hand control back to the page.
  useEffect(() => {
    if (expired.current || !onExpire) return;
    if (remaining <= 0) {
      expired.current = true;
      onExpire();
    }
  }, [remaining, onExpire]);

  const overtime = remaining <= 0;
  const warning = !overtime && remaining <= 300;
  const tone = overtime
    ? 'bg-red-600 text-white'
    : warning
      ? 'bg-amber-500 text-white'
      : 'bg-white/20 text-white';

  return (
    <div
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1 font-mono font-bold tabular-nums transition-colors ${tone} ${compact ? 'text-xs' : 'text-sm'}`}
      data-testid="session-timer"
    >
      <Timer size={compact ? 13 : 15} className={overtime ? 'animate-pulse' : ''} />
      <span data-testid="session-timer-value">
        {overtime ? `+${fmt(-remaining)}` : fmt(remaining)}
      </span>
      <span className={`font-sans font-semibold ${compact ? 'text-[10px]' : 'text-xs'} opacity-90`}>
        {overtime
          ? t('انتهى الوقت', 'Time is up')
          : t(`من ${Number(minutes) || 30} دقيقة`, `of ${Number(minutes) || 30} min`)}
      </span>
    </div>
  );
};

export default SessionTimer;
